<template>
	<div class="layout-default">
		<app-header
			ref="header"
			:variant="headerOptions?.variant || 'standard'"
			:show-user-name="headerOptions?.showUserName !== false"
			:simplified="headerOptions?.simplified || false"
		>
			<template #tools>
				<slot name="header-tools">
					<div v-if="headline" class="app-header-title">{{ headline }}</div>
				</slot>
			</template>
		</app-header>

		<div class="app-content" :class="additionalClasses">
			<aside v-if="hasSidebar && isSticky" class="app-sidebar" data-v-sticky-container>
				<div ref="sticky" v-sticky="stickyOptions" class="app-sidebar__sticky-wrapper">
					<div class="app-sidebar__inner" data-v-sticky-inner>
						<component :is="sidebarComponent" v-bind="sidebarProps" />
					</div>
				</div>
			</aside>

			<aside v-else-if="hasSidebar && !isSticky" class="app-sidebar">
				<div class="app-sidebar__inner">
					<component :is="sidebarComponent" v-bind="sidebarProps" />
				</div>
			</aside>

			<main class="app-main">
				<slot />
			</main>
		</div>
	</div>
</template>

<script lang="ts">
import { isFunction } from 'lodash';
import { Component, defineComponent } from 'vue';

import AppHeader from '@/components/app/AppHeader.vue';
import { sticky } from '@/directives';
import { AnyObject } from '@/tools/types';

export default defineComponent({
	components: {
		AppHeader,
	},

	directives: {
		sticky,
	},

	props: {
		sidebar: {
			type: Object,
			required: false,
			default: null,
		},

		headerOptions: {
			type: Object,
			required: false,
			default: () => ({
				showUserName: true,
			}),
		},
	},

	computed: {
		sidebarComponent(): Component | null {
			return (this.sidebar as any)?.component;
		},

		sidebarProps(): AnyObject | null {
			return (this.sidebar as any)?.props;
		},

		hasSidebar(): boolean {
			return !!this.sidebar;
		},

		isSidebarSmall(): boolean {
			return this.hasSidebar && this.sidebar.small;
		},

		isSticky(): boolean {
			return this.hasSidebar && this.sidebar.sticky;
		},

		stickyOptions(): AnyObject {
			return {
				topSpacing: () => (this.$refs.header ? (this.$refs.header as any).$el.offsetHeight : 0),
			};
		},

		additionalClasses(): Array<string> {
			if (!this.hasSidebar) {
				return [];
			}

			return [this.isSidebarSmall ? 'app-content--has-sidebar-small' : 'app-content--has-sidebar'];
		},

		headline(): string | undefined {
			const headline = this.$route?.meta?.headline;

			return isFunction(headline) ? headline('') : headline;
		},
	},
});
</script>

<style lang="scss">
.layout-default {
	width: 100%;
	padding: 0 4.8rem;
	background-color: $color-light;

	@include max-lg-and-portrait {
		padding: 0 3rem;
	}

	.app-content {
		display: flex;
		grid-gap: 0 2.4rem;
		width: 100%;
		min-height: $view-height;
		padding-top: $header-height;

		&--has-sidebar,
		&--has-sidebar-small {
			@include min-md {
				display: grid;
			}
		}

		&--has-sidebar {
			grid-template-areas: 'sidebar main main';
			grid-template-columns: repeat(3, 1fr);
		}

		&--has-sidebar-small {
			grid-template-areas: 'sidebar main main main';
			grid-template-columns: repeat(4, 1fr);

			@include max-md-and-portrait {
				/* prettier-ignore */
				grid-template-areas: 'sidebar sidebar'
					'main main';
				grid-template-columns: repeat(2, 1fr);
				grid-template-rows: max-content;
			}
		}
	}

	.app-sidebar {
		grid-area: sidebar;

		&__sticky-wrapper {
			width: 100%;
		}

		&__inner {
			height: 100%;
		}
	}

	.app-main {
		grid-area: main;
		width: 100%;
	}
}
</style>
